<template>
  <div class="parameter-panel">
    <h3 class="panel-title">⚙️ 参数控制</h3>
    
    <div 
      v-for="param in parameters" 
      :key="param.name"
      class="parameter-item"
    >
      <!-- 滑块类型 -->
      <div v-if="param.type === 'slider'" class="param-slider">
        <label :for="`param-${param.name}`" class="param-label">
          {{ param.label }}<span v-if="param.unit" class="input-unit"> ({{ param.unit }})</span>
        </label>
        <div class="slider-container">
          <button class="step-btn" @click="decrementParam(param)">-</button>
          <input 
            :id="`param-${param.name}`"
            type="range"
            :min="param.min"
            :max="param.max"
            :step="param.step || 1"
            :value="modelValue[param.name]"
            @input="updateParam(param.name, $event.target.value, param)"
            class="slider-input"
          />
          <button class="step-btn" @click="incrementParam(param)">+</button>
          <input
            class="value-input"
            type="number"
            :min="param.min"
            :max="param.max"
            :step="param.step || 'any'"
            :value="formatDisplayValue(modelValue[param.name], param)"
            @input="updateParam(param.name, $event.target.value, param)"
            @blur="handleInputBlur($event, param)"
          />
        </div>
        <p v-if="param.description" class="param-description">
          {{ param.description }}
        </p>
      </div>
      
      <!-- 输入框类型 -->
      <div v-else-if="param.type === 'input'" class="param-input">
        <label :for="`param-${param.name}`" class="param-label">
          {{ param.label }}<span v-if="param.unit" class="input-unit"> ({{ param.unit }})</span>
        </label>
        <div class="input-container">
          <input 
            :id="`param-${param.name}`"
            type="number"
            :min="param.min"
            :max="param.max"
            :step="param.step || 'any'"
            :value="modelValue[param.name]"
            @input="updateParam(param.name, $event.target.value, param)"
            class="number-input"
          />
        </div>
        <p v-if="param.description" class="param-description">
          {{ param.description }}
        </p>
      </div>
      
      <!-- 下拉选择类型 -->
      <div v-else-if="param.type === 'select'" class="param-select">
        <label :for="`param-${param.name}`" class="param-label">
          {{ param.label }}
        </label>
        <select 
          :id="`param-${param.name}`"
          :value="modelValue[param.name]"
          @change="updateParam(param.name, $event.target.value, param)"
          class="select-input"
        >
          <option 
            v-for="opt in param.options" 
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
        <p v-if="param.description" class="param-description">
          {{ param.description }}
        </p>
      </div>
      
      <!-- 开关类型 -->
      <div v-else-if="param.type === 'switch'" class="param-switch">
        <div class="switch-container">
          <label :for="`param-${param.name}`" class="param-label">
            {{ param.label }}
          </label>
          <label class="switch">
            <input 
              :id="`param-${param.name}`"
              type="checkbox"
              :checked="modelValue[param.name]"
              @change="updateParam(param.name, $event.target.checked, param)"
            />
            <span class="switch-slider"></span>
          </label>
        </div>
        <p v-if="param.description" class="param-description">
          {{ param.description }}
        </p>
      </div>
    </div>
    
    <div class="panel-actions">
      <button @click="resetParams" class="reset-button">
        🔄 重置参数
      </button>
    </div>
  </div>
</template>

<script setup>
import { useToast } from '@/composables/useToast'
import { formatNumber } from '@/utils/helpers'

const props = defineProps({
  parameters: {
    type: Array,
    required: true
  },
  modelValue: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['update:modelValue'])
const toast = useToast()

function validateValue(value, param) {
  let numValue = parseFloat(value)
  
  // 检查是否为有效数字
  if (isNaN(numValue)) {
    return param.default
  }
  
  // 验证最小值
  if (param.min !== undefined && numValue < param.min) {
    numValue = param.min
  }
  
  // 验证最大值
  if (param.max !== undefined && numValue > param.max) {
    numValue = param.max
  }
  
  // 应用步长，并修复浮点数精度问题
  if (param.step && param.step !== 'any') {
    const step = parseFloat(param.step)
    // 计算步数
    const steps = Math.round(numValue / step)
    // 重新计算值，避免浮点数误差
    numValue = steps * step
    // 进一步修复精度：根据 step 的小数位数进行 round
    const stepStr = String(step)
    let decimals = 0
    if (stepStr.includes('e') || stepStr.includes('E')) {
      const match = stepStr.match(/e([+-]?\d+)/i)
      if (match) {
        const exp = parseInt(match[1])
        decimals = Math.max(0, -exp + 2)
      }
    } else if (stepStr.includes('.')) {
      const parts = stepStr.split('.')
      decimals = parts[1] ? parts[1].length : 0
    }
    const factor = Math.pow(10, decimals)
    numValue = Math.round(numValue * factor) / factor
  }
  
  return numValue
}

function updateParam(name, value, param) {
  let processedValue = value
  
  // 对数值类型参数进行验证
  if (param.type === 'slider' || param.type === 'input') {
    processedValue = validateValue(value, param)
  } else if (param.type === 'select') {
    // 确保选择的值在选项中
    const validOption = param.options?.find(opt => opt.value == value)
    processedValue = validOption ? validOption.value : param.default
  }
  
  emit('update:modelValue', {
    ...props.modelValue,
    [name]: processedValue
  })
}

function incrementParam(param) {
  const step = parseFloat(param.step || 1)
  const current = parseFloat(props.modelValue[param.name] ?? param.default)
  const next = Math.min(current + step, param.max ?? current + step)
  updateParam(param.name, next, param)
}

function decrementParam(param) {
  const step = parseFloat(param.step || 1)
  const current = parseFloat(props.modelValue[param.name] ?? param.default)
  const next = Math.max(current - step, param.min ?? current - step)
  updateParam(param.name, next, param)
}

function resetParams() {
  // 显示确认提示
  if (confirm('确定要重置所有参数到默认值吗？')) {
    const defaults = {}
    props.parameters.forEach(p => {
      defaults[p.name] = p.default
    })
    emit('update:modelValue', defaults)
    toast.success('参数已重置到默认值', '重置成功')
  }
}

// 格式化显示值，用于输入框显示
function formatDisplayValue(value, param) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return value
  }
  
  // 根据 step 参数决定小数位数
  if (param.step && param.step !== 'any') {
    return formatNumber(value, null, parseFloat(param.step), false)
  }
  
  // 默认保留合理的小数位数
  return formatNumber(value, 2, null, false)
}

// 处理输入框失焦事件，确保值被正确格式化
function handleInputBlur(event, param) {
  const value = parseFloat(event.target.value)
  if (!isNaN(value)) {
    const validated = validateValue(value, param)
    event.target.value = formatDisplayValue(validated, param)
  }
}
</script>

<style scoped>
.parameter-panel {
  width: 100%;
}

.panel-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 1.5rem 0;
}

.parameter-item {
  margin-bottom: 1.5rem;
}

.parameter-item:last-of-type {
  margin-bottom: 2rem;
}

.param-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #555;
  margin-bottom: 0.5rem;
}

.param-description {
  font-size: 0.75rem;
  color: #777;
  margin: 0.5rem 0 0 0;
  line-height: 1.4;
}

/* 滑块样式 */
.slider-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.step-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
}

.slider-input {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: #ddd;
  outline: none;
  -webkit-appearance: none;
}

.slider-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #2196F3;
  cursor: pointer;
  transition: background 0.2s;
}

.slider-input::-webkit-slider-thumb:hover {
  background: #1976D2;
}

.slider-input::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #2196F3;
  cursor: pointer;
  border: none;
  transition: background 0.2s;
}

.slider-input::-moz-range-thumb:hover {
  background: #1976D2;
}

.param-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #333;
  min-width: 80px;
  text-align: right;
}

.value-input {
  width: 100px;
  padding: 0.375rem 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
}

/* 输入框样式 */
.input-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.number-input {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.875rem;
  transition: border-color 0.2s;
}

.number-input:focus {
  outline: none;
  border-color: #2196F3;
}

.input-unit {
  font-size: 0.875rem;
  color: #666;
  min-width: 40px;
}

/* 下拉选择样式 */
.select-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;
}

.select-input:focus {
  outline: none;
  border-color: #2196F3;
}

/* 开关样式 */
.switch-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.switch-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 24px;
}

.switch-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

.switch input:checked + .switch-slider {
  background-color: #2196F3;
}

.switch input:checked + .switch-slider:before {
  transform: translateX(24px);
}

/* 重置按钮 */
.panel-actions {
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
}

.reset-button {
  width: 100%;
  padding: 0.625rem 1rem;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #555;
  cursor: pointer;
  transition: all 0.2s;
}

.reset-button:hover {
  background: #f5f5f5;
  border-color: #bbb;
}

.reset-button:active {
  transform: scale(0.98);
}

/* 移动端优化 */
@media (max-width: 768px) {
  .panel-title {
    font-size: 1rem;
  }

  .parameter-item {
    margin-bottom: 1.25rem;
  }

  /* 增大触摸目标 */
  .slider-input {
    height: 8px;
  }

  .slider-input::-webkit-slider-thumb {
    width: 24px;
    height: 24px;
  }

  .slider-input::-moz-range-thumb {
    width: 24px;
    height: 24px;
  }

  .number-input,
  .select-input {
    padding: 0.75rem;
    font-size: 1rem;
  }

  .switch {
    width: 56px;
    height: 28px;
  }

  .switch-slider:before {
    height: 22px;
    width: 22px;
  }

  .switch input:checked + .switch-slider:before {
    transform: translateX(28px);
  }

  .reset-button {
    padding: 0.75rem 1rem;
    font-size: 1rem;
  }
}
</style>
